import { type Nullable } from "../types";
import { Observable, type Observer } from "../Misc/observable";
import { type IDisposable, type Scene } from "../scene";
import { type Camera } from "../Cameras/camera";
import { WebXRSessionManager } from "./webXRSessionManager";
import { WebXRCamera } from "./webXRCamera";
import { type WebXRRenderTarget, WebXRState } from "./webXRTypes";
import { WebXRFeatureName, WebXRFeaturesManager } from "./webXRFeaturesManager";
import { IsWebGPUXREngineCompatible, WebGPUXREngineNotCompatibleErrorMessage, WebGPUXRNotSupportedErrorMessage } from "./webXRGraphicsBinding";
import { Logger } from "../Misc/logger";
import { UniversalCamera } from "../Cameras/universalCamera.pure";
import { Quaternion, Vector3 } from "../Maths/math.vector.pure";
import { AbstractEngine } from "core/Engines/abstractEngine";

/**
 * Options for setting up XR spectator camera.
 */
// eslint-disable-next-line @typescript-eslint/naming-convention
export interface WebXRSpectatorModeOption {
    /**
     * Expected refresh rate (frames per sec) for a spectator camera.
     */
    fps?: number;
    /**
     * The index of rigCameras array in a WebXR camera.
     */
    preferredCameraIndex?: number;
}

/**
 * Base set of functionality needed to create an XR experience (WebXRSessionManager, Camera, StateManagement, etc.)
 * @see https://doc.babylonjs.com/features/featuresDeepDive/webXR/webXRExperienceHelpers
 */
export class WebXRExperienceHelper implements IDisposable {
    private _nonVRCamera: Nullable<Camera> = null;
    private _attachedToElement: boolean = false;
    private _spectatorCamera: Nullable<UniversalCamera> = null;
    private _originalSceneAutoClear = true;
    private _supported = false;
    private _spectatorMode = false;
    private _lastTimestamp = 0;
    private _spectatorStateChangedObserver: Nullable<Observer<WebXRState>> = null;
    private _spectatorXRFrameObserver: Nullable<Observer<XRFrame>> = null;
    private _spectatorAfterRenderObserver: Nullable<Observer<Camera>> = null;

    /**
     * Camera used to render xr content
     */
    public camera: WebXRCamera;
    /** A features manager for this xr session */
    public featuresManager: WebXRFeaturesManager;
    /**
     * Observers registered here will be triggered after the camera's initial transformation is set
     * This can be used to set a different ground level or an extra rotation.
     *
     * Note that ground level is considered to be at 0. The height defined by the XR camera will be added
     * to the position set after this observable is done executing.
     */
    public onInitialXRPoseSetObservable = new Observable<WebXRCamera>();
    /**
     * Fires when the state of the experience helper has changed
     */
    public onStateChangedObservable = new Observable<WebXRState>();
    /** Session manager used to keep track of xr session */
    public sessionManager: WebXRSessionManager;
    /**
     * The current state of the XR experience (eg. transitioning, in XR or not in XR)
     */
    public state: WebXRState = WebXRState.NOT_IN_XR;

    /**
     * Creates a WebXRExperienceHelper
     * @param _scene The scene the helper should be created in
     */
    private constructor(private _scene: Scene) {
        this.sessionManager = new WebXRSessionManager(_scene);
        this.camera = new WebXRCamera("webxr", _scene, this.sessionManager);
        this.featuresManager = new WebXRFeaturesManager(this.sessionManager);
        this.sessionManager.onXRSessionInit.add(() => {
            if (this._scene.getEngine().isWebGPU && !this.featuresManager.getEnabledFeature(WebXRFeatureName.LAYERS)?.attached) {
                throw new Error("WebGPU XR could not attach the required WebXR Layers feature.");
            }
        });

        _scene.onDisposeObservable.addOnce(() => {
            this.dispose();
        });
    }

    /**
     * Creates the experience helper
     * @param scene the scene to attach the experience helper to
     * @returns a promise for the experience helper
     */
    public static async CreateAsync(scene: Scene): Promise<WebXRExperienceHelper> {
        const helper = new WebXRExperienceHelper(scene);
        return await helper.sessionManager
            .initializeAsync()
            // eslint-disable-next-line github/no-then
            .then(() => {
                helper._supported = true;
                return helper;
            })
            // eslint-disable-next-line github/no-then
            .catch((e) => {
                helper._setState(WebXRState.NOT_IN_XR);
                helper.dispose();
                throw e;
            });
    }

    /**
     * Disposes of the experience helper
     */
    public dispose() {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.exitXRAsync();
        this._clearSpectatorObservers();
        this.camera.dispose();
        this.onStateChangedObservable.clear();
        this.onInitialXRPoseSetObservable.clear();
        this.sessionManager.dispose();
        this._spectatorCamera?.dispose();
        if (this._nonVRCamera) {
            this._scene.activeCamera = this._nonVRCamera;
        }
    }

    /**
     * Enters XR mode (This must be done within a user interaction in most browsers eg. button click)
     * @param sessionMode options for the XR session
     * @param referenceSpaceType frame of reference of the XR session
     * @param renderTarget the output canvas that will be used to enter XR mode
     * @param sessionCreationOptions optional XRSessionInit object to init the session with
     * @returns promise that resolves after xr mode has entered
     */
    public async enterXRAsync(
        sessionMode: XRSessionMode,
        referenceSpaceType: XRReferenceSpaceType,
        renderTarget?: WebXRRenderTarget,
        sessionCreationOptions: XRSessionInit = {}
    ): Promise<WebXRSessionManager> {
        if (!this._supported) {
            // eslint-disable-next-line no-throw-literal
            throw "WebXR not supported in this browser or environment";
        }
        if (this._scene.getEngine().isWebGPU) {
            if (!IsWebGPUXREngineCompatible(this._scene.getEngine())) {
                throw new Error(WebGPUXREngineNotCompatibleErrorMessage);
            }
            if (!WebXRSessionManager.IsWebGPUXRSupported) {
                throw new Error(WebGPUXRNotSupportedErrorMessage);
            }
            if (!this.featuresManager.getEnabledFeature(WebXRFeatureName.LAYERS)) {
                throw new Error("WebGPU XR requires the WebXR Layers feature. Import and enable WebXRLayers before calling enterXRAsync.");
            }
            sessionCreationOptions = {
                ...sessionCreationOptions,
                requiredFeatures: sessionCreationOptions.requiredFeatures ? [...sessionCreationOptions.requiredFeatures] : undefined,
                optionalFeatures: sessionCreationOptions.optionalFeatures ? [...sessionCreationOptions.optionalFeatures] : undefined,
            };
        }
        renderTarget ??= this.sessionManager.getWebXRRenderTarget();
        this._setState(WebXRState.ENTERING_XR);
        if (referenceSpaceType !== "viewer" && referenceSpaceType !== "local") {
            sessionCreationOptions.optionalFeatures = sessionCreationOptions.optionalFeatures || [];
            sessionCreationOptions.optionalFeatures.push(referenceSpaceType);
        }
        sessionCreationOptions = await this.featuresManager._extendXRSessionInitObject(sessionCreationOptions);
        if (this._scene.getEngine().isWebGPU) {
            const requiredFeatures = sessionCreationOptions.requiredFeatures ? [...sessionCreationOptions.requiredFeatures] : [];
            if (!requiredFeatures.includes("layers")) {
                requiredFeatures.push("layers");
            }
            sessionCreationOptions = {
                ...sessionCreationOptions,
                requiredFeatures,
                optionalFeatures: sessionCreationOptions.optionalFeatures?.filter((feature) => feature !== "layers"),
            };
        }
        // we currently recommend "unbounded" space in AR (#7959)
        if (sessionMode === "immersive-ar" && referenceSpaceType !== "unbounded") {
            Logger.Warn("We recommend using 'unbounded' reference space type when using 'immersive-ar' session mode");
        }
        this._originalSceneAutoClear = this._scene.autoClear;
        this._nonVRCamera = this._scene.activeCamera;
        this._attachedToElement = !!this._nonVRCamera?.inputs?.attachedToElement;
        let sceneStateChanged = false;
        const sessionEndedObserver = this.sessionManager.onXRSessionEnded.add(
            () => {
                // when using the back button and not the exit button (default on mobile), the session is ending but the EXITING state was not set
                if (this.state !== WebXRState.EXITING_XR) {
                    this._setState(WebXRState.EXITING_XR);
                }
                if (sceneStateChanged) {
                    // Reset camera rigs output render target to ensure sessions render target is not drawn after it ends
                    for (const c of this.camera.rigCameras) {
                        c.outputRenderTarget = null;
                    }

                    // Restore scene settings
                    this._scene.autoClear = this._originalSceneAutoClear;
                    this._scene.activeCamera = this._nonVRCamera;
                    if (this._attachedToElement && this._nonVRCamera) {
                        this._nonVRCamera.attachControl(!!this._nonVRCamera.inputs.noPreventDefault);
                    }
                    if (sessionMode !== "immersive-ar" && this.camera.compensateOnFirstFrame) {
                        if ((<any>this._nonVRCamera).setPosition) {
                            (<any>this._nonVRCamera).setPosition(this.camera.position);
                        } else {
                            this._nonVRCamera!.position.copyFrom(this.camera.position);
                        }
                    }
                }

                this._setState(WebXRState.NOT_IN_XR);
            },
            undefined,
            // Restore the scene before feature observers in case one of them throws during teardown.
            true,
            undefined,
            true
        );
        // make sure that the session mode is supported
        try {
            await this.sessionManager.initializeSessionAsync(sessionMode, sessionCreationOptions);
            await this.sessionManager.setReferenceSpaceTypeAsync(referenceSpaceType);

            const xrRenderState: XRRenderStateInit = {
                // if maxZ is 0 it should be "Infinity", but it doesn't work with the WebXR API. Setting to a large number.
                depthFar: this.camera.maxZ || 10000,
                depthNear: this.camera.minZ,
            };

            // The layers feature will have already initialized the XR session's layers on session init.
            // WebGPU-XR is layers-only, while WebGL can continue to use the legacy base-layer path.
            if (!this._scene.getEngine().isWebGPU && !this.featuresManager.getEnabledFeature(WebXRFeatureName.LAYERS)) {
                const baseLayer = await renderTarget.initializeXRLayerAsync(this.sessionManager.session);
                xrRenderState.baseLayer = baseLayer;
            }

            this.sessionManager.updateRenderState(xrRenderState);
            // run the render loop
            this.sessionManager.runXRRenderLoop();
            // Switch the scene to the XR camera.
            sceneStateChanged = true;
            this._nonVRCamera?.detachControl();

            this._scene.activeCamera = this.camera;
            // do not compensate when AR session is used
            if (sessionMode !== "immersive-ar") {
                this._nonXRToXRCamera();
            } else {
                // Kept here, TODO - check if needed
                this._scene.autoClear = false;
                this.camera.compensateOnFirstFrame = false;
                // reset the camera's position to the origin
                this.camera.position.set(0, 0, 0);
                this.camera.rotationQuaternion.set(0, 0, 0, 1);
                this.onInitialXRPoseSetObservable.notifyObservers(this.camera);
            }

            // Vision Pro suspends the audio context when entering XR, so we resume it here if needed.
            AbstractEngine.audioEngine?._resumeAudioContextOnStateChange();

            // Wait until the first frame arrives before setting state to in xr
            this.sessionManager.onXRFrameObservable.addOnce(() => {
                this._setState(WebXRState.IN_XR);
            });
            return this.sessionManager;
        } catch (e) {
            if (this.sessionManager.inXRSession) {
                await this.sessionManager.exitXRAsync();
            }
            if (!this.sessionManager.inXRSession) {
                this.sessionManager.onXRSessionEnded.remove(sessionEndedObserver);
            }
            Logger.Log(e);
            Logger.Log(e.message);
            this._setState(this.sessionManager.inXRSession ? WebXRState.ENTERING_XR : WebXRState.NOT_IN_XR);
            throw e;
        }
    }

    /**
     * Exits XR mode and returns the scene to its original state
     * @returns promise that resolves after xr mode has exited
     */
    public async exitXRAsync() {
        const isSessionStarting = this.state === WebXRState.ENTERING_XR && this.sessionManager.inXRSession;
        if (this.state !== WebXRState.IN_XR && !isSessionStarting) {
            return;
        }
        const previousState = this.state;
        this._setState(WebXRState.EXITING_XR);
        await this.sessionManager.exitXRAsync();
        if (this.sessionManager.inXRSession) {
            this._setState(previousState);
        }
    }

    /**
     * Enable spectator mode for desktop VR experiences.
     * When spectator mode is enabled a camera will be attached to the desktop canvas and will
     * display the first rig camera's view on the desktop canvas.
     * Please note that this will degrade performance, as it requires another camera render.
     * It is also not recommended to enable this in devices like the quest, as it brings no benefit there.
     * @param options giving WebXRSpectatorModeOption for specutator camera to setup when the spectator mode is enabled.
     */
    public enableSpectatorMode(options?: WebXRSpectatorModeOption): void {
        if (!this._spectatorMode) {
            this._spectatorMode = true;
            this._switchSpectatorMode(options);
        }
    }

    /**
     * Disable spectator mode for desktop VR experiences.
     */
    public disableSpecatatorMode(): void {
        if (this._spectatorMode) {
            this._spectatorMode = false;
            this._switchSpectatorMode();
        }
    }

    private _clearSpectatorObservers() {
        this.sessionManager.onXRFrameObservable.remove(this._spectatorXRFrameObserver);
        this._spectatorXRFrameObserver = null;
        this._scene.onAfterRenderCameraObservable.remove(this._spectatorAfterRenderObserver);
        this._spectatorAfterRenderObserver = null;
        this.onStateChangedObservable.remove(this._spectatorStateChangedObserver);
        this._spectatorStateChangedObserver = null;
    }

    private _switchSpectatorMode(options?: WebXRSpectatorModeOption): void {
        this._clearSpectatorObservers();

        const fps = options?.fps ? options.fps : 1000.0;
        const refreshRate = (1.0 / fps) * 1000.0;
        const cameraIndex = options?.preferredCameraIndex ? options?.preferredCameraIndex : 0;

        const updateSpectatorCamera = () => {
            if (this._spectatorCamera) {
                const delta = this.sessionManager.currentTimestamp - this._lastTimestamp;
                if (delta >= refreshRate) {
                    this._lastTimestamp = this.sessionManager.currentTimestamp;
                    this._spectatorCamera.position.copyFrom(this.camera.rigCameras[cameraIndex].globalPosition);
                    this._spectatorCamera.rotationQuaternion?.copyFrom(this.camera.rigCameras[cameraIndex].absoluteRotation);
                }
            }
        };
        if (this._spectatorMode) {
            if (cameraIndex >= this.camera.rigCameras.length) {
                throw new Error("the preferred camera index is beyond the length of rig camera array.");
            }
            const onStateChanged = () => {
                if (this.state === WebXRState.IN_XR) {
                    this._spectatorCamera?.dispose();
                    this._spectatorCamera = new UniversalCamera("webxr-spectator", Vector3.Zero(), this._scene);
                    this._spectatorCamera.rotationQuaternion = new Quaternion();
                    this._scene.activeCameras = [this.camera, this._spectatorCamera];
                    this._spectatorXRFrameObserver = this.sessionManager.onXRFrameObservable.add(updateSpectatorCamera);
                    this._spectatorAfterRenderObserver = this._scene.onAfterRenderCameraObservable.add((camera) => {
                        if (camera === this.camera) {
                            // reset the dimensions object for correct resizing
                            this._scene.getEngine().framebufferDimensionsObject = null;
                        }
                    });
                } else if (this.state === WebXRState.EXITING_XR) {
                    this.sessionManager.onXRFrameObservable.remove(this._spectatorXRFrameObserver);
                    this._spectatorXRFrameObserver = null;

                    this._scene.onAfterRenderCameraObservable.remove(this._spectatorAfterRenderObserver);
                    this._spectatorAfterRenderObserver = null;
                    this._spectatorCamera?.dispose();
                    this._spectatorCamera = null;
                    this._scene.activeCameras = null;
                }
            };
            this._spectatorStateChangedObserver = this.onStateChangedObservable.add(onStateChanged);
            onStateChanged();
        } else {
            this._spectatorCamera?.dispose();
            this._spectatorCamera = null;
            this._scene.activeCameras = [this.camera];
        }
    }

    private _nonXRToXRCamera() {
        this.camera.setTransformationFromNonVRCamera(this._nonVRCamera!);
        this.onInitialXRPoseSetObservable.notifyObservers(this.camera);
    }

    private _setState(val: WebXRState) {
        if (this.state === val) {
            return;
        }
        this.state = val;
        this.onStateChangedObservable.notifyObservers(this.state);
    }
}
